import React, { useEffect, useState } from "react";
import { Plus, Trash2, Settings, Pencil, Loader2, Lock } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

// Built-in system fields — visible in the registry, label-only rename supported.
const SYSTEM_FIELDS = {
  contact: [
    { name: "company_name", label: "Company Name", type: "text" },
    { name: "title", label: "Title", type: "text" },
    { name: "contact_person", label: "Contact Person", type: "text" },
    { name: "designation", label: "Designation", type: "text" },
    { name: "email", label: "Email", type: "text" },
    { name: "phone", label: "Phone", type: "text" },
    { name: "website", label: "Website", type: "text" },
    { name: "product_name", label: "Product Name", type: "text" },
    { name: "volume", label: "Volume", type: "text" },
    { name: "remarks", label: "Remarks", type: "text" },
    { name: "tags", label: "Tags", type: "text" },
  ],
  deal: [
    { name: "title", label: "Deal Title", type: "text" },
    { name: "contact_id", label: "Contact", type: "text" },
    { name: "stage", label: "Stage", type: "text" },
    { name: "estimated_value", label: "Estimated Deal Value", type: "number" },
    { name: "currency", label: "Currency", type: "text" },
    { name: "follow_up_date", label: "Follow-up Date", type: "date" },
    { name: "po_number", label: "Customer PO Number", type: "text" },
    { name: "pi_number", label: "Our PI Number", type: "text" },
    { name: "actual_value", label: "Actual Deal Value", type: "number" },
  ],
};

function EditFieldDialog({ field, onClose, onSaved }) {
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (field) setLabel(field.label || ""); }, [field]);

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim()) { toast.error("Label cannot be empty"); return; }
    setSaving(true);
    try {
      await api.put(`/fields/${field.id}`, { label: label.trim() });
      toast.success("Field renamed");
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!field} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Rename Field</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Display Label</Label>
            <Input data-testid="rename-label" value={label} onChange={(e) => setLabel(e.target.value)} required autoFocus />
            <p className="text-xs text-muted-foreground mt-1">
              This is the label shown on Contacts / Deals forms and tables. Renaming updates it everywhere instantly. Existing stored values are preserved (the internal machine name <code className="text-[10px]">{field?.name}</code> never changes).
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="rename-save" disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ManageFields() {
  const [customFields, setCustomFields] = useState([]);
  const [f, setF] = useState({ entity: "contact", name: "", label: "", type: "text" });
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [entityView, setEntityView] = useState("contact");

  const load = async () => { const r = await api.get("/fields"); setCustomFields(r.data); };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!f.name || !f.label) return;
    try {
      await api.post("/fields", f);
      toast.success("Field added");
      setF({ ...f, name: "", label: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/fields/${deleting.id}`);
      toast.success("Field removed");
      setDeleting(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
    finally { setDeleteBusy(false); }
  };

  // Merge system + custom fields for display, respecting the entityView toggle.
  const rows = [
    ...SYSTEM_FIELDS[entityView].map((s) => ({ ...s, id: `sys-${s.name}`, entity: entityView, system: true })),
    ...customFields.filter((c) => c.entity === entityView).map((c) => ({ ...c, system: false })),
  ];

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2"><Settings className="w-6 h-6 text-primary" />Manage Fields</h1>
        <p className="text-sm text-muted-foreground">Rename or remove any field on Contacts and Deals. Renaming updates the label everywhere in the app instantly.</p>
      </div>

      <form onSubmit={add} className="bg-white rounded-md border border-border p-5 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-5">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">Add a new custom field</div>
        </div>
        <div>
          <Label>Entity</Label>
          <select data-testid="field-entity" value={f.entity} onChange={(e) => setF({ ...f, entity: e.target.value })}
            className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
            <option value="contact">Contact</option>
            <option value="deal">Deal</option>
          </select>
        </div>
        <div><Label>Internal name</Label><Input data-testid="field-name" placeholder="e.g. region" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div><Label>Display label</Label><Input data-testid="field-label" placeholder="e.g. Region" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} /></div>
        <div>
          <Label>Type</Label>
          <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}
            className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="date">Date</option>
          </select>
        </div>
        <Button type="submit" data-testid="field-add" className="bg-primary hover:bg-primary/90 text-white h-10"><Plus className="w-4 h-4 mr-1" />Add Field</Button>
      </form>

      <div className="bg-white rounded-md border border-border overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-heading font-semibold">All Active Fields</h3>
          <div className="flex bg-neutral-100 rounded-md p-0.5">
            {[["contact", "Contacts"], ["deal", "Deals"]].map(([v, l]) => (
              <button key={v} data-testid={`view-${v}`} onClick={() => setEntityView(v)}
                className={`px-3 py-1 text-xs font-semibold rounded transition-colors ${entityView === v ? "bg-white text-primary shadow-sm" : "text-neutral-500 hover:text-neutral-800"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[11px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-5 py-3">Display Label</th>
              <th className="text-left px-5 py-3">Internal Name</th>
              <th className="text-left px-5 py-3">Type</th>
              <th className="text-left px-5 py-3">Source</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="fields-list">
            {rows.map((cf) => (
              <tr key={cf.id} className="border-t border-border">
                <td className="px-5 py-3 font-semibold">{cf.label}</td>
                <td className="px-5 py-3 text-xs font-mono text-muted-foreground">{cf.name}</td>
                <td className="px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground">{cf.type}</td>
                <td className="px-5 py-3">
                  {cf.system
                    ? <Badge variant="secondary" className="text-[10px]"><Lock className="w-3 h-3 mr-1" />System</Badge>
                    : <Badge className="bg-primary text-white hover:bg-primary/90 text-[10px]">Custom</Badge>}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {cf.system ? (
                      <span title="System fields have a fixed internal name and cannot be removed. To rename this, contact your admin.">
                        <Button size="sm" variant="ghost" disabled className="opacity-40 cursor-not-allowed"><Pencil className="w-3.5 h-3.5" /></Button>
                      </span>
                    ) : (
                      <>
                        <Button data-testid={`edit-field-${cf.id}`} size="sm" variant="ghost" onClick={() => setEditing(cf)} title="Rename label">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button data-testid={`delete-field-${cf.id}`} size="sm" variant="ghost"
                          onClick={() => setDeleting(cf)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10" title="Remove">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-secondary/50 border border-border rounded-md p-4 text-xs text-muted-foreground space-y-1">
        <div className="font-semibold text-neutral-800">How it works</div>
        <div>• <b>System fields</b> are the built-in columns Sun Nutrafoods relies on (Company Name, Stage, etc.). Their internal name is locked so integrations keep working, but they're always visible.</div>
        <div>• <b>Custom fields</b> can be renamed (label only — stored values stay intact) or removed at any time. When removed, existing values on records aren't erased; they just stop displaying.</div>
        <div>• Any rename here is reflected in the Contacts / Deals forms and tables the moment you save.</div>
      </div>

      <EditFieldDialog field={editing} onClose={() => setEditing(null)} onSaved={load} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove this field?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to remove the custom field <span className="font-semibold text-neutral-900">{deleting?.label}</span>.
              <br /><br />
              The field will disappear from the Contacts / Deals forms and tables immediately. Values already stored on existing records are preserved in the database but will no longer be displayed. You can re-create a field with the same internal name later to see them again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-field-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-field-confirm" onClick={confirmDelete} disabled={deleteBusy}
              className="bg-destructive text-white hover:bg-destructive/90">
              {deleteBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
